import type React from 'react'
import { createPortal } from 'react-dom'
import { Button } from './Button'
import { getNlsString } from '../../nls/nlsRuntime'
import './printSheet.css'

export type PrintSheetProps = {
  open: boolean
  onClose: () => void
  children: React.ReactNode
  printLabel?: string
  closeLabel?: string
}

/**
 * PrintSheet
 *
 * A full-page print preview container rendered in a portal on document.body.
 * Under `@media print`, hides the `#root` application shell and reveals only the printable content.
 * On screen, provides preview toolbar controls (Print, Close).
 *
 * Use for:
 * - Document, slip, receipt, report, or certificate printing that requires full-page printable layout.
 * - Situations requiring browser native printing (`window.print()`) while hiding the main app UI.
 *
 * Don't use for:
 * - In-app modal dialogs or standard forms (use AppDialog or BottomSheet instead).
 * - Small inline print buttons that don't need a custom print viewport.
 */
export const PrintSheet: React.FC<PrintSheetProps> = ({
  open,
  onClose,
  children,
  printLabel,
  closeLabel,
}) => {
  if (!open) return null

  const handlePrint = () => {
    window.print()
  }

  const resolvedPrintLabel = printLabel ?? getNlsString('NLSCommonActionPrint')
  const resolvedCloseLabel = closeLabel ?? getNlsString('NLSCommonActionClose')

  return createPortal(
    <div className="printSheet" data-testid="print-sheet" role="dialog" aria-modal="true">
      <div className="printSheet__toolbar" data-testid="print-sheet-toolbar">
        <Button
          variant="primary"
          size="sm"
          data-testid="print-sheet-print-btn"
          onClick={handlePrint}
        >
          {resolvedPrintLabel}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          data-testid="print-sheet-close-btn"
          onClick={onClose}
        >
          {resolvedCloseLabel}
        </Button>
      </div>
      <div className="printSheet__body">
        {children}
      </div>
    </div>,
    document.body,
  )
}
